// src/pages/Signup.jsx
import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './Auth.module.css';

export default function Signup() {
    const navigate = useNavigate();
    const { login } = useAuth();

    // 회원 유형: 개인 / 조직 / 교육기관
    const [mode, setMode] = useState('personal'); // 'personal' | 'org' | 'edu'
    const [orgRole, setOrgRole] = useState('member'); // 'admin' | 'member'

    // 요금제
    // 0:system, 1:Basic(종량제), 2:Personal(정액제),
    // 3:Team, 4:Business, 5:Enterprise, 6:Enterprise Plus
    const [plan, setPlan] = useState('2');

    // 공통 필드
    const [userEmail, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [userName, setName] = useState('');

    // 조직/교육기관 공용 필드(기관명)
    const [orgName, setOrgName] = useState('');
    const [orgStatus, setOrgStatus] = useState('idle'); // idle | checking | exists | notExists | error
    const [orgMsg, setOrgMsg] = useState('');
    const latestOrgReqId = useRef(0);
    const orgAbortRef = useRef(null);

    // 검증 규칙
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const passwordRule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/;

    const emailValid = !userEmail || emailRegex.test(userEmail);
    const pwValid = passwordRule.test(password);
    const pwMatch = password && password2 && password === password2;

    const isOrgLike = mode === 'org' || mode === 'edu';

    /* ------------------------------ 기관(조직/교육기관) 존재/중복 체크 (디바운스) ------------------------------ */
    useEffect(() => {
        if (!isOrgLike || !orgName.trim()) {
            setOrgStatus('idle');
            setOrgMsg('');
            if (orgAbortRef.current) orgAbortRef.current.abort();
            return;
        }

        setOrgStatus('checking');
        // admin: 중복 확인 / member: 존재 확인 (org, edu 모두 동일)
        setOrgMsg(orgRole === 'admin' ? '기관 중복 확인 중…' : '기관 존재 확인 중…');

        const reqId = ++latestOrgReqId.current;
        const controller = new AbortController();
        orgAbortRef.current = controller;

        const t = setTimeout(async () => {
            try {
                const res = await fetch('/api/account/existTenant', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ org_name: orgName.trim() }),
                    signal: controller.signal,
                });
                if (reqId !== latestOrgReqId.current) return;
                if (!res.ok) throw new Error('server');

                const data = await res.json();
                if (data.exists) {
                    setOrgStatus('exists');
                    setOrgMsg(orgRole === 'admin' ? '이미 존재하는 기관입니다. 다른 이름을 사용하세요.' : '기관이 확인되었습니다.');
                } else {
                    setOrgStatus('notExists');
                    setOrgMsg(orgRole === 'admin' ? '사용 가능한 기관 이름입니다.' : '존재하지 않는 기관입니다. 관리자에게 문의하세요.');
                }
            } catch (e) {
                if (e.name === 'AbortError') return;
                setOrgStatus('error');
                setOrgMsg('기관 확인 중 오류가 발생했습니다.');
            }
        }, 450);

        return () => {
            clearTimeout(t);
            controller.abort();
        };
    }, [isOrgLike, orgRole, orgName]);

    /* ------------------------------ 제출 가능 여부 ------------------------------ */
    const canSubmit = useMemo(() => {
        if (!emailValid || !pwValid || !pwMatch || !userName.trim()) return false;

        if (mode === 'personal') {
            return ['1', '2'].includes(plan);
        }

        // 조직/교육기관 공통: 기관명 필수
        if (!orgName.trim()) return false;

        if (orgRole === 'admin') {
            // org: 요금제 필수 + notExists
            if (mode === 'org') {
                const orgPlanValid = ['3', '4', '5', '6'].includes(plan);
                return orgStatus === 'notExists' && orgPlanValid;
            }
            // edu: 요금제 선택 없음 + notExists
            return orgStatus === 'notExists';
        }

        // member: exists
        return orgStatus === 'exists';
    }, [mode, orgRole, emailValid, pwValid, pwMatch, userName, orgName, orgStatus, plan]);

    /* ------------------------------ 모드/역할 전환 핸들러 ------------------------------ */
    function handleModeChange(nextMode) {
        setMode(nextMode);

        if (nextMode === 'personal') {
            if (!['1', '2'].includes(plan)) setPlan('2');
            setOrgName('');
            setOrgStatus('idle');
            setOrgMsg('');
            return;
        }

        // org / edu 공통 초기화
        setOrgRole('admin');
        setOrgStatus('idle');
        setOrgMsg('');

        if (nextMode === 'org') {
            if (!['3', '4', '5', '6'].includes(plan)) setPlan('3');
        } else if (nextMode === 'edu') {
            // edu는 요금제 선택 UI 없음 → plan은 서버에서 무시/기본값 처리하도록 0으로 고정
            setPlan('0');
        }
    }

    function handleOrgRoleChange(nextRole) {
        setOrgRole(nextRole);
        setOrgStatus('idle');
        setOrgMsg('');

        if (mode === 'org' && nextRole === 'admin') {
            if (!['3', '4', '5', '6'].includes(plan)) setPlan('3');
        }
        if (mode === 'edu') {
            // edu는 요금제 선택이 없으므로 plan은 0 유지
            setPlan('0');
        }
    }

    /* ------------------------------ 제출 처리 ------------------------------ */
    async function handleSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;

        try {
            // 역할 매핑
            const role = mode === 'personal' ? 'PERSONAL' : orgRole === 'admin' ? 'ADMIN' : 'USER';

            // planValue 결정
            let planValue;
            if (mode === 'personal') {
                planValue = plan; // 1/2
            } else if (mode === 'org') {
                planValue = orgRole === 'admin' ? plan : '0'; // admin만 3~6, member는 0
            } else {
                // edu: 요금제 선택 없음 → 서버가 기관 기준으로 처리
                planValue = '0';
            }

            const basePayload = {
                user_id: userEmail.trim(),
                password,
                name: userName.trim(),
                email: userEmail.trim(),
                plan: planValue,
                role,
            };

            const payload =
                mode === 'personal'
                    ? {
                          ...basePayload,
                          tenant_id: userEmail.trim(),
                      }
                    : {
                          ...basePayload,
                          tenant_id: orgName.trim(), // org/edu 모두 기관명 = tenant
                      };

            const res = await fetch('/api/account/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (data.status === 200) {
                alert('회원 가입에 성공하였습니다.');
                login(userName.trim());
                navigate('/dashboard');
            } else {
                alert('회원 가입에 실패하였습니다.');
            }
        } catch {
            alert('네트워크 오류가 발생했습니다.');
        }
    }

    /* ------------------------------ 렌더링 ------------------------------ */
    return (
        <main className={styles.container}>
            <div className={styles.card}>
                <h2 className={styles.title}>회원가입</h2>

                {/* 개인 / 조직 / 교육기관 선택 */}
                <div className={styles.segmented}>
                    <button type="button" className={`${styles.option} ${mode === 'personal' ? styles.optionActive : ''}`} onClick={() => handleModeChange('personal')}>
                        개인
                    </button>
                    <button type="button" className={`${styles.option} ${mode === 'org' ? styles.optionActive : ''}`} onClick={() => handleModeChange('org')}>
                        조직
                    </button>
                    <button type="button" className={`${styles.option} ${mode === 'edu' ? styles.optionActive : ''}`} onClick={() => handleModeChange('edu')}>
                        교육기관
                    </button>
                </div>

                {/* 조직/교육기관 역할 선택 (중앙 배치) */}
                {isOrgLike && (
                    <>
                        <h3 className={styles.sectionTitle}>{mode === 'org' ? '조직 역할 선택' : '교육기관 역할 선택'}</h3>
                        <div className={`${styles.roleChips} ${styles.roleChipsCenter} ${styles.roleSelect}`}>
                            <label className={`${styles.roleChip} ${orgRole === 'admin' ? styles.roleChipActive : ''}`}>
                                <input type="radio" name="org-role" value="admin" checked={orgRole === 'admin'} onChange={() => handleOrgRoleChange('admin')} />
                                <span className={styles.roleChipDot}></span>
                                관리자
                            </label>
                            <label className={`${styles.roleChip} ${orgRole === 'member' ? styles.roleChipActive : ''}`}>
                                <input type="radio" name="org-role" value="member" checked={orgRole === 'member'} onChange={() => handleOrgRoleChange('member')} />
                                <span className={styles.roleChipDot}></span>
                                사용자
                            </label>
                        </div>
                    </>
                )}

                {/* 개인 요금제 선택 */}
                {mode === 'personal' && (
                    <section className={styles.planSection}>
                        <h3 className={styles.sectionTitle}>개인 요금제 선택</h3>
                        <div className={styles.planGridPersonal}>
                            <label className={`${styles.planCard} ${plan === '1' ? styles.planCardActive : ''}`}>
                                <input type="radio" name="personal-plan" value="1" checked={plan === '1'} onChange={() => setPlan('1')} />
                                <div className={styles.planHeader}>
                                    <span className={styles.planName}>Basic Plan</span>
                                    <span className={styles.planTag}>종량제</span>
                                </div>
                                <p className={styles.planDesc}>사용한 만큼만 과금되는 유연한 요금제입니다.</p>
                            </label>

                            <label className={`${styles.planCard} ${plan === '2' ? styles.planCardActive : ''}`}>
                                <input type="radio" name="personal-plan" value="2" checked={plan === '2'} onChange={() => setPlan('2')} />
                                <div className={styles.planHeader}>
                                    <span className={styles.planName}>Personal Plan</span>
                                    <span className={styles.planTag}>정액제</span>
                                </div>
                                <p className={styles.planDesc}>매월 일정 금액으로 안심하고 사용할 수 있는 개인용 정액제입니다.</p>
                            </label>
                        </div>
                    </section>
                )}

                {/* 조직 요금제 선택 (관리자만, edu는 없음) */}
                {mode === 'org' && orgRole === 'admin' && (
                    <section className={styles.planSection}>
                        <h3 className={styles.sectionTitle}>조직 요금제 선택</h3>
                        <div className={styles.planGridOrg}>
                            <label className={`${styles.planCard} ${plan === '3' ? styles.planCardActive : ''}`}>
                                <input type="radio" name="org-plan" value="3" checked={plan === '3'} onChange={() => setPlan('3')} />
                                <div className={styles.planHeader}>
                                    <span className={styles.planName}>Team Plan</span>
                                    <span className={styles.planTag}>5명 이하</span>
                                </div>
                                <p className={styles.planDesc}>
                                    소규모 팀을 위한 요금제입니다. 기본 요금 대비 <strong>5% 할인</strong>이 적용됩니다.
                                </p>
                            </label>

                            <label className={`${styles.planCard} ${plan === '4' ? styles.planCardActive : ''}`}>
                                <input type="radio" name="org-plan" value="4" checked={plan === '4'} onChange={() => setPlan('4')} />
                                <div className={styles.planHeader}>
                                    <span className={styles.planName}>Business Plan</span>
                                    <span className={styles.planTag}>50명 이하</span>
                                </div>
                                <p className={styles.planDesc}>
                                    중견 규모 조직을 위한 플랜입니다. 기본 요금 대비 <strong>15% 할인</strong>이 적용됩니다.
                                </p>
                            </label>

                            <label className={`${styles.planCard} ${plan === '5' ? styles.planCardActive : ''}`}>
                                <input type="radio" name="org-plan" value="5" checked={plan === '5'} onChange={() => setPlan('5')} />
                                <div className={styles.planHeader}>
                                    <span className={styles.planName}>Enterprise Plan</span>
                                    <span className={styles.planTag}>100명 이하</span>
                                </div>
                                <p className={styles.planDesc}>
                                    대규모 조직에 최적화된 플랜입니다. 기본 요금 대비 <strong>30% 할인</strong>이 적용됩니다.
                                </p>
                            </label>

                            <label className={`${styles.planCard} ${plan === '6' ? styles.planCardActive : ''}`}>
                                <input type="radio" name="org-plan" value="6" checked={plan === '6'} onChange={() => setPlan('6')} />
                                <div className={styles.planHeader}>
                                    <span className={styles.planName}>Enterprise Plus</span>
                                    <span className={styles.planTag}>100명 초과</span>
                                </div>
                                <p className={styles.planDesc}>100명 이상 초대형 조직을 위한 맞춤형 플랜입니다. 별도 상담을 통해 견적이 산정됩니다.</p>
                            </label>
                        </div>
                    </section>
                )}

                {/* org/edu 사용자 안내 */}
                {isOrgLike && orgRole === 'member' && (
                    <p className={styles.hintText}>
                        {mode === 'org' ? '조직 사용자는 조직 관리자가 선택한 요금제가 자동으로 적용됩니다.' : '교육기관 사용자는 교육기관 정책에 따라 요금제가 자동 적용됩니다.'}
                    </p>
                )}

                {/* ------------------------------ 폼 ------------------------------ */}
                <form className={styles.form} onSubmit={handleSubmit} noValidate>
                    {/* 기관 이름 (org/edu) */}
                    {isOrgLike && (
                        <label className={styles.inputRow}>
                            <span className={styles.inputLabel}>{mode === 'org' ? '조직 이름' : '교육기관 이름'}</span>
                            <input
                                type="text"
                                placeholder={mode === 'org' ? '예) realtime-tech' : '예) firsim-university'}
                                value={orgName}
                                onChange={(e) => setOrgName(e.target.value.trim())}
                                className={styles.input}
                            />
                            {orgStatus !== 'idle' && (
                                <div
                                    className={
                                        orgStatus === 'checking'
                                            ? styles.hint
                                            : (orgRole === 'admin' && orgStatus === 'notExists') || (orgRole === 'member' && orgStatus === 'exists')
                                              ? styles.hint
                                              : styles.error
                                    }
                                >
                                    {orgMsg}
                                </div>
                            )}
                        </label>
                    )}

                    {/* 이메일 */}
                    <label className={styles.inputRow}>
                        <span className={styles.inputLabel}>이메일</span>
                        <input
                            type="email"
                            placeholder="you@example.com"
                            value={userEmail}
                            onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))}
                            className={`${styles.input} ${!emailValid ? styles.invalid : ''}`}
                        />
                        {!emailValid && <div className={styles.error}>이메일 형식이 올바르지 않습니다.</div>}
                    </label>

                    {/* 비밀번호 */}
                    <label className={styles.inputRow}>
                        <span className={styles.inputLabel}>비밀번호</span>
                        <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className={styles.input} />
                        {password && !pwValid && <div className={styles.error}>8자 이상, 영문 대/소문자, 숫자, 특수문자를 모두 포함해야 합니다.</div>}
                        {password && pwValid && <div className={styles.hint}>사용 가능한 비밀번호입니다.</div>}
                    </label>

                    {/* 비밀번호 확인 */}
                    <label className={styles.inputRow}>
                        <span className={styles.inputLabel}>비밀번호 확인</span>
                        <input type="password" placeholder="••••••••" value={password2} onChange={(e) => setPassword2(e.target.value)} className={styles.input} />
                        {password2 && !pwMatch && <div className={styles.error}>비밀번호가 일치하지 않습니다.</div>}
                    </label>

                    {/* 사용자 이름 */}
                    <label className={styles.inputRow}>
                        {orgRole === 'admin' && <span className={styles.inputLabel}>관리자 이름</span>}
                        {orgRole !== 'admin' && <span className={styles.inputLabel}>사용자 이름</span>}
                        <input type="text" value={userName} onChange={(e) => setName(e.target.value)} className={styles.input} />
                    </label>

                    <button className={styles.submit} type="submit" disabled={!canSubmit}>
                        회원가입
                    </button>
                </form>
            </div>
        </main>
    );
}
